import {useEffect} from 'react';
import {useIntl} from '@sail/intl';

import {getDynamicSolanaChainIdFromNetwork} from '@stripe-internal/wallet-connect-wrapper';

import {getChainIdFromNetwork} from 'src/components/Wallet/constants';
import {EVM_NETWORKS} from 'src/types';
import {setValue, useSelector} from 'src/v2/state/store';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {handleError} from 'src/v2/components/error/handleError';

export const useNetworkSwitchingEffect = () => {
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const chainId = useSelector(
    (state) => state.walletConnection.activeWallet?.chainId,
  );
  const network = useSelector(
    (state) => state.walletConnection.activeWallet?.network,
  );
  const livemode = useSelector((state) => state.livemode);
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const walletConnectSupportedCurrencies = useSelector(
    (state) => state.walletConnectSupportedCurrencies,
  );
  const isAnyCryptoWalletConnect = useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto_wallet_connect,
  );

  useEffect(() => {
    const switchNetworkIfMismatched = async () => {
      if (!chainId || !network) {
        return;
      }

      let targetChainId: number | null = null;

      if (isAnyCryptoWalletConnect && selectedToken) {
        const tokenOnCurrentNetwork = walletConnectSupportedCurrencies.some(
          (c) => c.assetCode === selectedToken && c.currencyNetwork === network,
        );
        // Token is usable on the wallet's current network — nothing to switch.
        if (tokenOnCurrentNetwork) {
          return;
        }

        // Otherwise switch to the first compatible network for the token.
        const compatibleCurrency = walletConnectSupportedCurrencies.find(
          (c) =>
            c.assetCode === selectedToken &&
            EVM_NETWORKS.includes(c.currencyNetwork),
        );
        if (compatibleCurrency) {
          targetChainId = getChainIdFromNetwork(
            compatibleCurrency.currencyNetwork,
            livemode,
          );
        }
      }

      // Fall back to the wallet's own reported network.
      if (targetChainId === null) {
        targetChainId =
          network === 'solana'
            ? getDynamicSolanaChainIdFromNetwork(network, livemode)
            : getChainIdFromNetwork(network, livemode);
      }

      // Already on the target chain — nothing to switch.
      if (targetChainId === chainId) {
        return;
      }

      setValue((state) => {
        state.overlay = 'NETWORK_SWITCH';
      });

      try {
        await walletClient.switchNetwork(targetChainId);
      } catch (error) {
        handleError({error, intl, context: 'network_switch'});
      } finally {
        setValue((state) => {
          state.overlay = null;
        });
      }
    };

    switchNetworkIfMismatched();
  }, [
    chainId,
    network,
    livemode,
    walletClient,
    intl,
    selectedToken,
    walletConnectSupportedCurrencies,
    isAnyCryptoWalletConnect,
  ]);
};
